import EulaDrawer from '../components/EulaDrawer';
import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  ChevronRight, 
  Lock,
  Mail,
  Shield,
  Bell,
  Palette,
  Check,
  Save,
  Handshake,
  KeyRound
} from 'lucide-react';
import "../../../styles/Settings.css";
import { useAuth } from '../context/AuthContext';
import { storageService } from '../service/storage';

// ==========================================
// COMPONENTE: SettingsSection (Reutilizable)
// ==========================================


const SettingsSection = ({ handlesave,title, icon: Icon, save = false, children,isSuccess }) => (
  <div className="settings-section">
    <div className="settings-section-header">
      <div className="settings-section-group">
        <div className="settings-section-icon">
          <Icon size={20} />
        </div>
        <h3 className="settings-section-title">{title}</h3>
      </div>
      {save && (
        <button 
          className={`save-settings-btn ${isSuccess ? 'success' : ''}`} 
          onClick={() => {handlesave()}}
          disabled={isSuccess}
        >
          {isSuccess ? (
            <>
              <Check size={18} />
              <span>¡Guardado!</span>
            </>
          ) : (
            <>
              <Save size={18} />
              <span>Guardar</span>
            </>
          )}
        </button>
      )}
    </div>
    <div className="settings-section-content">
      {children}
    </div>
  </div>
);

// ==========================================
// COMPONENTE: ToggleSwitch
// ==========================================
const ToggleSwitch = ({ enabled, onChange, label, description }) => (
  <div className="toggle-item">
    <div className="toggle-info">
      <span className="toggle-label">{label}</span>
      {description && <span className="toggle-description">{description}</span>}
    </div>
    <button 
      className={`toggle-switch ${enabled ? 'enabled' : ''}`}
      onClick={() => onChange(!enabled)}
      aria-label={enabled ? 'Desactivar' : 'Activar'}
    >
      <div className="toggle-thumb" />
    </button>
  </div>
);

// ==========================================
// COMPONENTE: SettingsView (Vista Principal)
// ==========================================


const SettingsView = ({ onPasswordChange, onEmailChange }) => {

  const {directSetPass,setChangeEmail,getEula} = useAuth();
  const storage = storageService.getSettings();
  
  const [eulaOpen, setEulaOpen] = useState(false);
  const [eulaContent, setEulaContent] = useState(null);
  const [eulaLoading, setEulaLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [dashboardSections, setDashboardSections] = useState({
    dailyStats: { enabled: storage.dailySec, label: 'Estadísticas Diarias', description: 'Muestra las ventas y métricas del día' },
    workers: { enabled: storage.workersSec, label: 'Trabajadores', description: 'Rendimiento individual de trabajadores' },
    organismos: {enabled: storage.organismosSec, label: 'Organismos', description: 'Métricas del día'},
    hourlyChart: { enabled: storage.hourGraphSec, label: 'Gráfico por Horas', description: 'Gráfico de ventas horarias' },
    topVendidos: { enabled: storage.topVentasSec, label: 'Top Ventas', description: 'Top productos vendidos'},
    topRecepcionados: { enabled: storage.topRecepSec, label: 'Top Recepcionados', description: 'Top productos recepcionados'},
    inventory: { enabled: storage.resumeInventorySec, label: 'Resumen de Inventario', description: 'Stock y productos bajo mínimos' },
    teams: { enabled: storage.teamSec, label: 'Equipos', description: 'Visualización de equipos y sus ventas' },
  });

  const formatEulaToHtml = (text) => {
    if (!text) return '';
    
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      
      .replace(/^\*\*(.+?)\*\*$/gm, '<h1 class="eula-main-title">$1</h1>')
      .replace(/^__(.+?)__$/gm, '<h1 class="eula-main-title">$1</h1>')
      
      .replace(/^##\s*(.+?)$/gm, '<h2 class="eula-section-title">$1</h2>')
      .replace(/^\d+\.\s+(.+?)$/gm, '<h3 class="eula-subsection-title">$1</h3>')
      
      .replace(/^###\s*(.+?)$/gm, '<h4 class="eula-subsection-title">$1</h4>')
      
      .replace(/^\*\s+(.+?)$/gm, '<li class="eula-list-item">$1</li>')
      
      .replace(/^---$/gm, '<hr class="eula-divider">')
      
      .replace(/^(?!<[hl]|<li)(.+?)$/gm, '<p class="eula-paragraph">$1</p>')
      
      .replace(/(<li class="eula-list-item">.+?<\/li>\n?)+/g, '<ul class="eula-list">$&</ul>')
      
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\n/g, '');

    return html;
  };

  const handleOpenEula = async () => {
    setEulaOpen(true);
    setEulaLoading(true);
    try {
      const response = await getEula();
      const rawText = response.data;
      const formattedHtml = formatEulaToHtml(rawText); // ← aquí aplicas el formateo
      setEulaContent(formattedHtml); // ← ahora pasas HTML formateado
    } catch (err) {
      setEulaContent(null);
    } finally {
      setEulaLoading(false);
    }
  };

  const handleSaveChanges = () => {
    storageService.setSettings(
      dashboardSections.dailyStats.enabled,
      dashboardSections.workers.enabled,
      dashboardSections.organismos.enabled,
      dashboardSections.hourlyChart.enabled,
      dashboardSections.topVendidos.enabled,
      dashboardSections.topRecepcionados.enabled,
      dashboardSections.inventory.enabled,
      dashboardSections.teams.enabled
    );
    
    // Activar estado de éxito
    setSaveSuccess(true);
    
    // Resetear después de 2 segundos
    setTimeout(() => {
      setSaveSuccess(false);
    }, 2000);
    
    console.log("cambiar variables de settings");
  };



  // Estado para configuración de cuenta
  const [accountSettings, setAccountSettings] = useState({
    notifications: false,
    twoFactor: false,
    compactMode: false
  });

  // Handler para cambiar visibilidad de secciones
  const handleSectionToggle = (sectionKey) => {
    setDashboardSections(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        enabled: !prev[sectionKey].enabled
      }
    }));
  };

  // Handler para guardar cambios (simulado)
  return (
    <div className="settings-view">
      <div className="settings-header">
        <div>
          <h1>Configuración</h1>
          <p className="settings-subtitle">Personaliza tu experiencia y gestiona tu cuenta</p>
        </div>
      </div>

      <div className="settings-grid">
        {/* PANEL DE CONTROL */}
        <SettingsSection handlesave={handleSaveChanges} title="Panel de Control" icon={LayoutDashboard} save = {true} isSuccess={saveSuccess}>
          <div className="settings-description">
            Habilita o deshabilita las secciones que deseas ver en tu dashboard principal
          </div>
          <div className="toggles-list">
            {Object.entries(dashboardSections).map(([key, section]) => (
              <ToggleSwitch
                key={key}
                label={section.label}
                description={section.description}
                enabled={section.enabled}
                onChange={() => handleSectionToggle(key)}
              />
            ))}
          </div>
        </SettingsSection>

        {/* SEGURIDAD Y ACCESO */}
        <SettingsSection title="Seguridad y Acceso" icon={Shield}>
          <div className="security-options">
            <button className="security-btn" onClick={() => {directSetPass()}}>
              <div className="security-btn-icon">
                <Lock size={20} />
              </div>
              <div className="security-btn-info">
                <span className="security-btn-label">Cambiar Contraseña</span>
                <span className="security-btn-hint">Actualiza tu contraseña de acceso</span>
              </div>
              <ChevronRight size={18} className="security-btn-arrow" />
            </button>

            <button className="security-btn" onClick={() => {setChangeEmail(true)}}>
              <div className="security-btn-icon">
                <Mail size={20} />
              </div>
              <div className="security-btn-info">
                <span className="security-btn-label">Cambiar Correo Electrónico</span>
                <span className="security-btn-hint">Modifica tu dirección de email</span>
              </div>
              <ChevronRight size={18} className="security-btn-arrow" />
            </button>

            {/*<div className="divider" />

            <ToggleSwitch
              label="Autenticación de dos factores"
              description="Añade una capa extra de seguridad a tu cuenta"
              enabled={accountSettings.twoFactor}
              onChange={(enabled) => setAccountSettings(prev => ({ ...prev, twoFactor: enabled }))}
            />*/}
          </div>
        </SettingsSection>

        { /*PREFERENCIAS (Extensible para futuro) */
          <SettingsSection title="Privacidad y preferencias" icon={KeyRound}>
            <div className="toggles-list">
              {/*
              <ToggleSwitch
                label="Notificaciones push"
                description="Recibe alertas sobre ventas y stock"
                enabled={accountSettings.notifications}
                onChange={(enabled) => setAccountSettings(prev => ({ ...prev, notifications: enabled }))}
              />
              <ToggleSwitch
                label="Modo compacto"
                description="Reduce el espaciado entre elementos"
                enabled={accountSettings.compactMode}
                onChange={(enabled) => setAccountSettings(prev => ({ ...prev, compactMode: enabled }))}
              />
              */}

              <button className="security-btn" onClick={handleOpenEula}>
                <div className="security-btn-icon">
                  <Handshake size={20} />
                </div>
                <div className="security-btn-info">
                  <span className="security-btn-label">EULA</span>
                  <span className="security-btn-hint">Políticas de privacidad</span>
                </div>
                <ChevronRight size={18} className="security-btn-arrow" />
              </button>
            </div>
          </SettingsSection>
        }
      </div>
      <EulaDrawer
        isOpen={eulaOpen}
        onClose={() => setEulaOpen(false)}
        content={eulaContent}
        loading={eulaLoading}
      />
    </div>
  );
};

export default SettingsView;